import debounce from 'lodash/debounce'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import type { LOCATION_SEARCH_TYPE } from '@/types'

import { fetchAiQuerySuggestions, getAiSuggestionLabel } from './aiSearch'
import {
  AI_SEARCH_MIN_QUERY_LENGTH,
  AI_SEARCH_TRY_ASKING_CHIPS,
  hasAiSearchMinLength,
} from './aiSearchConstants'

export type SuggestionChip = {
  key: string
  label: string
  suggestion: LOCATION_SEARCH_TYPE | null
}

export type SuggestionsStatus = 'loading' | 'chips' | 'error' | 'empty'

const SUGGESTION_FETCH_DEBOUNCE_MS = 100

function isAbortError(error: unknown) {
  return typeof error === 'object' && error !== null && 'name' in error && error.name === 'AbortError'
}

export function buildSuggestionChips(
  fetchQuery: string,
  suggestions: LOCATION_SEARCH_TYPE[],
): SuggestionChip[] {
  if (!hasAiSearchMinLength(fetchQuery)) {
    return AI_SEARCH_TRY_ASKING_CHIPS.map((label) => ({
      key: `default-${label}`,
      label,
      suggestion: null,
    }))
  }

  return suggestions
    .map((item, index) => ({
      key: item.idWithType ?? `${item.id}-${index}`,
      label: getAiSuggestionLabel(item),
      suggestion: item,
    }))
    .filter((chip) => chip.label.length > 0)
}

function getSuggestionsStatus({
  hasTypedQuery,
  isFetchingSuggestions,
  hasFetchError,
  chipCount,
}: {
  hasTypedQuery: boolean
  isFetchingSuggestions: boolean
  hasFetchError: boolean
  chipCount: number
}): SuggestionsStatus {
  if (hasTypedQuery && isFetchingSuggestions) return 'loading'
  if (hasTypedQuery && hasFetchError) return 'error'
  if (hasTypedQuery && chipCount === 0) return 'empty'
  return 'chips'
}

export function useAiSearchSuggestions() {
  const [fetchQuery, setFetchQuery] = useState('')
  const [suggestions, setSuggestions] = useState<LOCATION_SEARCH_TYPE[]>([])
  const [isFetchingSuggestions, setIsFetchingSuggestions] = useState(false)
  const [hasFetchError, setHasFetchError] = useState(false)

  const abortControllerRef = useRef<AbortController | null>(null)
  const mountedRef = useRef(true)
  const debouncedFetchRef = useRef<ReturnType<typeof debounce> | null>(null)

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
      abortControllerRef.current?.abort()
    }
  }, [])

  const fetchSuggestions = useCallback(async (searchQuery: string) => {
    abortControllerRef.current?.abort()
    const controller = new AbortController()
    abortControllerRef.current = controller

    setIsFetchingSuggestions(true)
    setHasFetchError(false)
    try {
      const items = await fetchAiQuerySuggestions({
        query: searchQuery,
        signal: controller.signal,
      })

      if (!mountedRef.current || abortControllerRef.current !== controller) return

      setSuggestions(items)
    } catch (error) {
      if (isAbortError(error) || !mountedRef.current || abortControllerRef.current !== controller) return
      setSuggestions([])
      setHasFetchError(true)
    } finally {
      if (mountedRef.current && abortControllerRef.current === controller) {
        setIsFetchingSuggestions(false)
      }
    }
  }, [])

  useEffect(() => {
    debouncedFetchRef.current?.cancel()
    debouncedFetchRef.current = debounce((searchQuery: string) => {
      void fetchSuggestions(searchQuery)
    }, SUGGESTION_FETCH_DEBOUNCE_MS)

    return () => {
      debouncedFetchRef.current?.cancel()
      abortControllerRef.current?.abort()
    }
  }, [fetchSuggestions])

  useEffect(() => {
    const trimmedQuery = fetchQuery.trim()

    if (trimmedQuery.length < AI_SEARCH_MIN_QUERY_LENGTH) {
      abortControllerRef.current?.abort()
      debouncedFetchRef.current?.cancel()
      setSuggestions([])
      setIsFetchingSuggestions(false)
      setHasFetchError(false)
      return
    }

    debouncedFetchRef.current?.(trimmedQuery)
  }, [fetchQuery])

  const suggestionChips = useMemo(
    () => buildSuggestionChips(fetchQuery, suggestions),
    [fetchQuery, suggestions],
  )

  const hasTypedQuery = hasAiSearchMinLength(fetchQuery)
  const status = getSuggestionsStatus({
    hasTypedQuery,
    isFetchingSuggestions,
    hasFetchError,
    chipCount: suggestionChips.length,
  })

  const onTypedQueryChange = useCallback((value: string) => {
    setFetchQuery(value)
    setHasFetchError(false)

    if (hasAiSearchMinLength(value)) {
      setIsFetchingSuggestions(true)
      return
    }

    abortControllerRef.current?.abort()
    debouncedFetchRef.current?.cancel()
    setSuggestions([])
    setIsFetchingSuggestions(false)
  }, [])

  const cancelPendingFetch = useCallback(() => {
    debouncedFetchRef.current?.cancel()
  }, [])

  return {
    fetchQuery,
    suggestionChips,
    hasTypedQuery,
    status,
    isSubmitDisabledBySuggestions: hasTypedQuery && status !== 'chips',
    onTypedQueryChange,
    cancelPendingFetch,
  }
}
